/**
 * BoardOnboarding — 게시판 소개 온보딩 페이지
 *
 * 채널에 게시판이 아직 없을 때 표시되는 안내 페이지.
 * 게시판 유형별 활용 방법을 소개하고 게시판 만들기 CTA를 제공합니다.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import {
  LayoutList, FileText, Megaphone,
  BookOpen, Plus,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

interface BoardOnboardingProps {
  onCreateBoard?: () => void;
}

const BoardOnboarding: React.FC<BoardOnboardingProps> = ({ onCreateBoard }) => {
  const boardTypes = [
    {
      icon: FileText,
      title: <Trans>General</Trans>,
      desc: <Trans>A free-form board for sharing updates, ideas, and discussions with your team.</Trans>,
    },
    {
      icon: Megaphone,
      title: <Trans>Announcements</Trans>,
      desc: <Trans>Post important announcements and company-wide notices that everyone should see.</Trans>,
    },
    {
      icon: BookOpen,
      title: <Trans>Wiki</Trans>,
      desc: <Trans>Organize documents and guides with categories. Build your team knowledge base.</Trans>,
    },
  ];

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
      <div className="max-w-md w-full flex flex-col items-center gap-8">
        {/* 아이콘 + 타이틀 */}
        <div className="flex flex-col items-center gap-3">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-emerald-400/20 to-teal-500/20 flex items-center justify-center">
            <LayoutList className="size-8 text-primary" />
          </div>
          <h2 className="text-lg font-bold text-[color:rgb(var(--text-title))]">
            <Trans>Boards</Trans>
          </h2>
          <p className="text-sm text-muted-foreground text-center leading-relaxed max-w-[320px]">
            <Trans>
              Create boards to organize information for your channel.
              Choose a board type that fits your needs.
            </Trans>
          </p>
        </div>

        {/* 게시판 유형 카드 */}
        <div className="w-full flex flex-col gap-3">
          {boardTypes.map((bt, i) => (
            <div
              key={i}
              className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50"
            >
              <div className="size-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0 mt-0.5">
                <bt.icon className="size-4 text-primary" />
              </div>
              <div className="min-w-0">
                <h4 className="text-label font-semibold text-[color:rgb(var(--text-title))] mb-0.5">
                  {bt.title}
                </h4>
                <p className="text-meta text-muted-foreground leading-relaxed">
                  {bt.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 게시판 만들기 버튼 — 생성 권한 있을 때만(onCreateBoard 주어질 때) */}
        {onCreateBoard && (
        <Button
          onClick={onCreateBoard}
          className="gap-2 px-6"
        >
          <Plus className="size-4" />
          <Trans>Create Board</Trans>
        </Button>
        )}
      </div>
    </div>
  );
};

export default BoardOnboarding;
